"use client";

import React, { useState } from "react";
import styled from "styled-components";
import { useTranslations } from "next-intl";
import { SCContainerContent } from "@/utils/styledComponents";
import { theme } from "@/config/theme";

// Fixed components - removed size prop since they don't use it
const Plus = () => <span style={{ fontSize: "20px" }}>＋</span>;
const Minus = () => <span style={{ fontSize: "20px" }}>－</span>;

type FaqItem = {
    questionKey: string;
    answerKey: string;
};

type FaqsAccordionBlockProps = {
    items: FaqItem[];
    namespace?: string;
};

const FaqsAccordionBlock: React.FC<FaqsAccordionBlockProps> = ({
    items,
    namespace = "Faqs"
}) => {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const t = useTranslations(namespace);

    const tx = useTranslations("FaqsKidsSwimming");
    console.log(tx, "tx");

    const toggle = (index: number) => {
        setActiveIndex(index === activeIndex ? null : index);
    };

    return (
        <SCFaqsSection>
            <SCFaqsContainer theme={theme}>
                <h2 className="section-title">{t("title")}</h2>
                <div className="accordion-list">
                    {items.map((item, index) => (
                        <SCAccordionItem key={index}>
                            <SCQuestion onClick={() => toggle(index)}>
                                <span>{t(item.questionKey)}</span>
                                {activeIndex === index ? <Minus /> : <Plus />}
                            </SCQuestion>
                            <SCAnswer $active={activeIndex === index}>
                                {renderFaqAnswer(t(item.answerKey))}
                            </SCAnswer>
                        </SCAccordionItem>
                    ))}
                </div>
            </SCFaqsContainer>
        </SCFaqsSection>
    );
};

export default FaqsAccordionBlock;

const SCFaqsSection = styled.section`
    background-color: #fff;
    padding: 60px 0;
    display: flex;
    justify-content: center;
`;

const SCFaqsContainer = styled(SCContainerContent)`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    .section-title {
        font-size: 28px;
        font-weight: 600;
        margin-bottom: 32px;
        color: #111;
        text-align: center;

        @media screen and (max-width: 768px) {
            font-size: 22px;
            text-align: left;
        }
    }

    .accordion-list {
        width: 100%;
        max-width: 800px;
    }
`;

const SCAccordionItem = styled.div`
    border-bottom: 1px solid #ccc;
    padding-bottom: 0px;
    padding-top: 10px;
`;

const SCQuestion = styled.button`
    background: none;
    border: none;
    padding: 0;
    width: 100%;
    font-size: 16px;
    font-weight: 500;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    text-align: left;
    color: #000;

    &:hover {
        color: #0073e6;
    }
`;

const SCAnswer = styled.div<{ $active: boolean }>`
    margin-top: 12px;
    font-size: 15px;
    line-height: 1.6;
    text-align: left;
    color: #555;
    max-height: ${({ $active }) => ($active ? "1000px" : "0")};
    overflow: hidden;
    transition: max-height 0.3s ease;
`;

function renderFaqAnswer(answer: string) {
    const parts = answer.split("||"); // Break into paragraphs

    return parts.map((line, index) => {
        const elements: JSX.Element[] = [];
        let lastIndex = 0;

        // Regex to find [text|url]
        const regex = /\[([^\|\]]+)\|([^\]]+)\]/g;
        let match;

        while ((match = regex.exec(line)) !== null) {
            const [fullMatch, linkText, linkUrl] = match;
            const beforeText = line.slice(lastIndex, match.index);
            if (beforeText) {
                elements.push(
                    <span key={`text-${index}-${lastIndex}`}>{beforeText}</span>
                );
            }
            elements.push(
                <a
                    key={`link-${index}-${match.index}`}
                    href={linkUrl.trim()}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {linkText.trim()}
                </a>
            );
            lastIndex = regex.lastIndex;
        }

        // Add remaining text after last match
        if (lastIndex < line.length) {
            elements.push(
                <span key={`end-${index}`}>{line.slice(lastIndex)}</span>
            );
        }

        return <p key={index}>{elements}</p>;
    });
}
